import React, { useState, useEffect } from "react";
import axios from "axios";
import CommonDatePicker from "./datepicker/CommonDatePicker";
import { useParams, useNavigate } from "react-router-dom";
import { format, startOfMonth, endOfMonth } from "date-fns";
import config from "./common/Config";
import { Link } from "react-router-dom";
import { useRole } from "../contexts/RoleContext";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../utils/companySettingsCache";
import { encryptPayload, normalizeEncryptedApiResponse } from "../utils/customerCrypto";
import RoleSwitcher from "./common/RoleSwitcher";
import { Box, Grid2, Typography } from '@mui/material';
import "./common/commonStyles.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const { prefix, userId } = useParams();
  const { activeRole, userRoles, isLoading: roleLoading, isRole } = useRole();
  const user_role = localStorage.getItem("user_role");
  const isManager = user_role == 8 || (activeRole && (activeRole.id == 8 || activeRole.id == "8")) || isRole(8);

  const today = new Date();
  const [dates, setDates] = useState([startOfMonth(today), endOfMonth(today)]);
  const [selectedRole, setSelectedRole] = useState("");
  const [statisticsData, setStatisticsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoleOption, setSelectedRoleOption] = useState(null);
  const [orderProcessSequence, setOrderProcessSequence] = useState(null);
  const company_tax_type = localStorage.getItem("company_tax_type");
  const currencySymbol = localStorage.getItem("currencySymbol");
  const token = localStorage.getItem("token");

  const handleDateChange = (value) => {
    setDates(value);

    if (value && value[0] && value[1]) {
      const s1 = value[0] ? format(value[0], 'yyyy-MM-dd') : '';
      const e1 = value[1] ? format(value[1], 'yyyy-MM-dd') : '';

      localStorage.setItem('startDate', s1);
      localStorage.setItem('endDate', e1);

      fetchDataWithDates(value);
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handleRoleSelectChange = (event, newValue) => {
    setSelectedRoleOption(newValue);
    const roleId = newValue ? newValue.value : "";
    setSelectedRole(roleId);
    if (dates && dates[0] && dates[1]) {
      fetchDataWithDates(dates);
    }
  };

  const fetchUserRoles = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      return;
    }

    const apiUserRolesUrl = `${config.API_URL}/get_users_role`;
    try {
      const response = await fetch(apiUserRolesUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user roles");
      }

      const data = await response.json();
      const formattedOptions = data.data.map(item => ({
        label: item.name,
        value: item.id,
      }));
      setRoleOptions(formattedOptions);
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  const fetchDashboardCount = async (dateRange) => {
    localStorage.setItem("accessToken", prefix);
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      window.location.href = "/react/login";
      return;
    }

    setIsLoading(true);

    const [startDate, endDate] = dateRange;
    const formattedStartDate = startDate ? format(startDate, "MM/dd/yyyy") : "";
    const formattedEndDate = endDate ? format(endDate, "MM/dd/yyyy") : "";
    const reportrange = (formattedStartDate && formattedEndDate) ? `${formattedStartDate} - ${formattedEndDate}` : "";

    const s1 = startDate ? format(startDate, "yyyy-MM-dd") : "";
    const e1 = endDate ? format(endDate, "yyyy-MM-dd") : "";

    localStorage.setItem("startDate", s1);
    localStorage.setItem("endDate", e1);

    const apiUrl = `${config.API_URL}/get_dashboard_count`;

    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const payload = {
        reportrange,
        role: selectedRole,
        prefix,
      };

      if (useEncrypt) {
        const ep = encryptPayload(payload);
        if (!ep) {
          return;
        }
        formData.append("encrypted_payload", ep);
      } else {
        formData.append("reportrange", reportrange);
        formData.append("role", selectedRole);
        formData.append("prefix", prefix);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch statistics data");
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      setStatisticsData(data);
    } catch (error) {
      console.error("Error fetching statistics data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataWithDates = async (dateRange) => {
    await fetchDashboardCount(dateRange);
  };

  const fetchData = async () => {
    await fetchDashboardCount(dates);
  };

  const fetchUserData = async () => {
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      if (useEncrypt) {
        const ep = encryptPayload({ id: userId, prefix });
        if (!ep) {
          return;
        }
        formData.append("encrypted_payload", ep);
      } else {
        formData.append("id", userId);
        formData.append("prefix", prefix);
      }
      const response = await axios.post(
        `${config.API_URL}/get_users_details_id`,
        formData,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );
      const result = normalizeEncryptedApiResponse(response.data, useEncrypt) ?? response.data;
      if (result.success == true) {
        localStorage.setItem("site_name", result.data.site_name);
        localStorage.setItem("first_name", result.data.first_name);
        localStorage.setItem("last_name", result.data.last_name);
        localStorage.setItem("isSinglePicking", result.data.isSinglePicking);
        localStorage.setItem("roleID", result.data.group_id);
        localStorage.setItem("shopifyConnectivity", result.data.shopifyConnectivity);
        localStorage.setItem("amazonConnectivity", result.data.amazonConnectivity);
        if (result.data.QuickBooksConnectivity != null) {
          localStorage.setItem(
            "QuickBooksConnectivity",
            result.data.QuickBooksConnectivity
          );
        }
        localStorage.setItem("showEmailTemplate", result.data.showEmailTemplate);
        localStorage.setItem("showContraPrice", result.data.showContraPrice);
        localStorage.setItem("showCessRate", result.data.showCessRate ?? "N");
        localStorage.setItem("grnExpiryDateIsMandatory", result.data.grnExpiryDateIsMandatory ?? "N");

        fetchDataWithDates(dates);
      } else {
        window.location.href = `${config.LOG_OUT}`;
      }
    } catch (error) {
      console.error("Error accepting order:", error);
    }
  };

  useEffect(() => {
    (async () => {
      if (prefix) {
        localStorage.setItem("accessToken", prefix);
        localStorage.setItem("userId", userId);
        fetchUserData();
        fetchUserRoles();
        getCompanySettings().then((settings) => {
          if (settings?.order_process_sequence !== undefined) {
            setOrderProcessSequence(String(settings.order_process_sequence));
          }
        });
      } else {
        window.location.href = `${config.LOG_OUT}`;
      }
    })();
  }, [prefix, userId]);

  useEffect(() => {
    if (activeRole) {
      setSelectedRole(activeRole.id);
      const roleOption = roleOptions.find(opt => opt.value === activeRole.id);
      if (roleOption) {
        setSelectedRoleOption(roleOption);
      }
    }
  }, [activeRole, roleOptions]);

  const getDisplayDates = () => {
    if (dates && dates[0] && dates[1]) {
      return {
        from: format(dates[0], "dd/MM/yyyy"),
        to: format(dates[1], "dd/MM/yyyy")
      };
    }
    return {
      from: statisticsData ? statisticsData.from_date : "0",
      to: statisticsData ? statisticsData.to_date : "0"
    };
  };

  const displayDates = getDisplayDates();

  useEffect(() => {
    const isTeleCallerRole = (role) => {
      if (!role) return false;
      const roleId = String(role.id || role.role_id || "").trim();
      if (roleId === "20") {
        return true;
      }
      
      if (roleId === "18") {
        navigate("/sales/epos/new");
      }
      const roleName = String(role.name || "").toLowerCase().replace(/\s+/g, "");
      return roleName.includes("telecaller") || roleName.includes("telecalling");
    };

    const handleRoleChanged = (event) => {
      const role = event?.detail?.role;
      if (isTeleCallerRole(role)) {
        navigate("/sales/quick-order");
      }
      
    };

    window.addEventListener("roleChanged", handleRoleChanged);
    return () => window.removeEventListener("roleChanged", handleRoleChanged);
  }, [navigate]);

  return (
      <Box className="main-container" sx={{ backgroundColor: '#f5f7fa' }}>
        <Box className="container-fluid" >
          <Box className="row">
            <Box>
              <Grid2 container spacing={2} justifyContent="space-between" alignItems="center">
                <Grid2 size={{xs: 12, md: 4}}></Grid2>
                <Grid2 size={{xs: 12, md: 8}}>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection={{xs: "column", md: "row"}}
                    gap={1.5}
                  >
                    {/* Role switcher */}
                    <RoleSwitcher
                      className="input-border"
                      sx={{
                        width: 170,
                        flexShrink: 0,
                      }}
                    />

                    {/* Date picker */}
                    <CommonDatePicker
                      className="input-border"
                      value={dates}
                      onChange={handleDateChange}
                      sx={{
                        width: 280,
                        flexShrink: 0,
                      }}
                    />

                    {/* Date text */}
                    <Box
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                        color: '#000',
                        flexShrink: 0,
                      }}
                    >
                      From: {displayDates.from} &nbsp; To: {displayDates.to}
                    </Box>
                  </Box>
                </Grid2>


              </Grid2>
              {isLoading ? (
                <Box className="loading-container" sx={{ textAlign: 'center', padding: '50px' }}>
                  <Box className="spinner-border" role="status">
                    <Typography component="span" className="sr-only">Loading...</Typography>
                  </Box>
                  <Typography>Loading dashboard data...</Typography>
                </Box>
              ) : isManager ? (
                <Box className="show-order-area dashboard" sx={{ backgroundColor: '#fff !important', p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Box>
                    <Typography variant="h2">
                      <span>
                        <i className="fa fa-th"></i> Statistics
                      </span>
                    </Typography>
                  </Box>
                  <Box className="col-12 col-lg-12 col-sm-12">
                    <Box className="row">
                      {orderProcessSequence !== "4" && (
                        <>
                          <Box className="col-xl-2 col-sm-4">
                            <Box className="info-card sales-card leaf-green">
                              <Box className="card-body">
                                <Link to="/dashboard/SalesOrder">
                                  <Typography component="p"> Sales </Typography>
                                  <Typography component="b" id="sales_order_grand_total">
                                    {currencySymbol} {statisticsData
                                      ? parseFloat(
                                        statisticsData.sales_grand_total
                                      ).toFixed(2)
                                      : "0.00"}
                                  </Typography>{" "}
                                  <Typography component="b" id="sales_order_item_tax">
                                    {company_tax_type} {currencySymbol} {statisticsData
                                      ? parseFloat(
                                        statisticsData.sales_item_tax
                                      ).toFixed(2)
                                      : "0.00"}
                                  </Typography>{" "}
                                </Link>
                              </Box>
                            </Box>
                          </Box>
                          <Box className="col-xl-2 col-sm-4">
                            <Box className="info-card sales-card leaf-green">
                              <Box className="card-body">
                                <Link to="#">
                                  <Typography component="p"> Accept  </Typography>
                                  <Typography component="b" id="sales_order_grand_total">
                                    {currencySymbol} {statisticsData
                                      ? parseFloat(
                                        statisticsData.accept_order_total
                                      ).toFixed(2)
                                      : "0.00"}
                                  </Typography>{" "}
                                  <Typography component="b" id="sales_order_item_tax">
                                    {company_tax_type} {currencySymbol} {statisticsData
                                      ? parseFloat(
                                        statisticsData.accept_order_tax
                                      ).toFixed(2)
                                      : "0.00"}
                                  </Typography>{" "}
                                </Link>
                              </Box>
                            </Box>
                          </Box>
                          <Box className="col-xl-2 col-sm-4">
                            <Box className="info-card sales-card leaf-green">
                              <Box className="card-body">
                                <Link to="/dashboard/InprogressInvoice">
                                  <Typography component="p"> Inprogress  </Typography>
                                  <Typography component="b" id="inprogress_grand_total">
                                    {currencySymbol} {statisticsData
                                      ? parseFloat(
                                        statisticsData.inprogress_invoice_grand_total

                                      ).toFixed(2)
                                      : "0.00"}
                                  </Typography>{" "}
                                  <Typography component="b" id="inprogress_item_tax">
                                    {company_tax_type}  {currencySymbol} {statisticsData
                                      ? parseFloat(
                                        statisticsData.inprogress_invoice_tax
                                      ).toFixed(2)
                                      : "0.00"}
                                  </Typography>{" "}
                                </Link>
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )}
                      <Box className="col-xl-2 col-sm-4">
                        <Box className="info-card sales-card tomatored">
                          <Box className="card-body">
                            <Link to="/dashboard/DeliveredInvoice">
                              <Typography component="p"> Delivered  </Typography>
                              <Typography component="b" id="delivered_invoice_grand_total">
                                {currencySymbol} {statisticsData
                                  ? parseFloat(
                                    statisticsData.delivered_invoice_grand_total
                                  ).toFixed(2)
                                  : "0.00"}
                              </Typography>{" "}
                              <Typography component="b" id="delivered_invoice_item_tax">
                                {company_tax_type} {currencySymbol} {statisticsData
                                  ? parseFloat(
                                    statisticsData.delivered_invoice_item_tax
                                  ).toFixed(2)
                                  : "0.00"}
                              </Typography>{" "}
                            </Link>
                          </Box>
                        </Box>
                      </Box>
                      {orderProcessSequence !== "4" && (
                        <Box className="col-xl-2 col-sm-4">
                          <Box className="info-card sales-card tomatored">
                            <Box className="card-body">
                              <Link to="/reports/missing_order">
                                <Typography component="p"> Cross Off </Typography>
                                <Typography component="b" id="cross_off_grand_total">
                                  {currencySymbol} {statisticsData
                                    ? parseFloat(
                                      statisticsData.crossOff_grand_total
                                    ).toFixed(2)
                                    : "0.00"}
                                </Typography>{" "}
                                <Typography component="b" id="cross_off_item_tax">
                                  {company_tax_type}  {currencySymbol} {statisticsData
                                    ? parseFloat(
                                      statisticsData.crossOff_item_tax
                                    ).toFixed(2)
                                    : "0.00"}
                                </Typography>{" "}
                              </Link>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      <Box className="col-xl-2 col-sm-4">
                        <Box className="info-card sales-card tomatored">
                          <Box className="card-body">
                            <Link to="#">
                              <Typography component="p"> Undeliver /Reject </Typography>
                              <Typography component="b" id="undelivered_order_grand_total">
                                {currencySymbol} {statisticsData
                                  ? parseFloat(
                                    statisticsData.undelivered_grand_total
                                  ).toFixed(2)
                                  : "0.00"}
                              </Typography>{" "}
                              <Typography component="b" id="undelivered_order_item_tax">
                                {company_tax_type} {currencySymbol} {statisticsData
                                  ? parseFloat(
                                    statisticsData.undelivered_item_tax
                                  ).toFixed(2)
                                  : "0.00"}
                              </Typography>{" "}
                            </Link>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {prefix !== "eve" && prefix !== "ve" && (
                        <>               
                  <Typography variant="h2">
                    <span>
                      <i className="fa fa-calendar"></i> DAILY MIS
                    </span>
                  </Typography>

                  <Box className="col-12 col-lg-12 col-sm-12">
                    <Box className="row">
                      {orderProcessSequence !== "4" && (
                        <>
                          <Box className="col-12 col-sm-4 col-lg-2">
                            <Link
                              to="/dashboard/NewSales"
                              className="bred white quick-button"
                            >
                              <Typography component="p" className="yellow">
                                New <br /> Sales
                              </Typography>
                              <Typography component="b">{statisticsData ? statisticsData.new_sales : 0}</Typography>
                            </Link>
                          </Box>
                          <Box className="col-12 col-sm-4 col-lg-2">
                            <Link
                              to="/dashboard/OrderAccepted"
                              className="blightOrange white quick-button"
                            >
                              <Typography component="p" className="yellow">
                                Order <br /> Accepted
                              </Typography>
                              <Typography component="b">{statisticsData ? statisticsData.sale_accept : 0}</Typography>
                            </Link>
                          </Box>
                          <Box className="col-12 col-sm-4 col-lg-2">
                            <Link
                              to="/dashboard/picklistGenerated"
                              className="bgrey white quick-button"
                            >
                              <Typography component="p" className="yellow">
                                Picklist <br /> Generated
                              </Typography>
                              <Typography component="b">{statisticsData ? statisticsData.picklistgenerated : 0}</Typography>
                            </Link>
                          </Box>
                          <Box className="col-12 col-sm-4 col-lg-2">
                            <Link
                              to="/dashboard/PickingConfirm"
                              className="blightBlue white quick-button"
                            >
                              <Typography component="p" className="yellow">
                                Picking <br /> Confirmed
                              </Typography>
                              <Typography component="b">
                                {statisticsData
                                  ? `${statisticsData.picking_confirmed} (${statisticsData.picking_confirmed_inv})`
                                  : 0}
                              </Typography>
                            </Link>
                          </Box>
                        </>
                      )}
                      <Box className="col-12 col-sm-4 col-lg-2">
                        <Link to="/dashboard/ManifestGenerated" className="bpink white quick-button">
                          <Typography component="p" className="yellow">
                            Manifest <br /> Generated
                          </Typography>
                          <Typography component="b">
                            {statisticsData
                              ? `${statisticsData.manifestgenerated} (${statisticsData.manifest_order})`
                              : 0}
                          </Typography>
                        </Link>
                      </Box>
                      <Box className="col-12 col-sm-4 col-lg-2">
                        <Link to="/dashboard/DeliveryConfirmed" className="bdarkGreen white quick-button">
                          <Typography component="p" className="yellow">
                            Delivery <br /> Confirmed
                          </Typography>
                          <Typography component="b">
                            {statisticsData
                              ? statisticsData.delivery_confirmed
                              : 0}
                          </Typography>
                        </Link>
                      </Box>
                      <Box className="col-12 col-sm-4 col-lg-2">
                        <Link
                          to="/dashboard/TopSales"
                          className="bred white quick-button"
                        >
                          <Typography component="p" className="yellow">
                            Top <br /> Sales
                          </Typography>
                          <Typography component="b" id="top_sales">
                            {statisticsData
                              ? `${statisticsData.top_sales
                              } ${currencySymbol} ${statisticsData.top_sales_amount.toFixed(2)}`
                              : 0.0}
                          </Typography>
                        </Link>
                      </Box>
                      <Box className="col-12 col-sm-4 col-lg-2">
                        <Link
                          to="/dashboard/MaxValueOrder"
                          className="bred white quick-button"
                        >
                          <Typography component="p" className="yellow">
                            Max Value <br /> Order
                          </Typography>
                          <Typography component="b" id="top_customer_sales">
                            {statisticsData
                              ? `${statisticsData.top_customer_sales
                              }  ${currencySymbol} ${statisticsData.top_customer_amount.toFixed(
                                2
                              )}`
                              : 0.0}
                          </Typography>
                        </Link>
                      </Box>
                    </Box>
                  </Box>
                  </>
                  )}
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
  );
};

export default Dashboard;
